import React, { useState, useEffect, useRef } from "react";
import { StaffSideMenu } from "../staff/StaffSideMenu";
import API_URL from "../../api/API";
import { useStaffAuthContext } from "../staff/useStaffAuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { AthleteTabMenu } from "./AthleteTabMenu";
import AthleteProfileCard from "../component/AthleteProfileCard";
import { RiDeleteBin4Line } from "react-icons/ri";

const LinkIcon = () => {
  return (
    <>
      <svg
        width="16"
        height="19"
        viewBox="0 0 16 19"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M11.667 4.16661L4.65534 11.3216C4.34289 11.6342 4.16736 12.058 4.16736 12.4999C4.16736 12.9419 4.34289 13.3657 4.65534 13.6783C4.96789 13.9907 5.39174 14.1662 5.83368 14.1662C6.27562 14.1662 6.69946 13.9907 7.01201 13.6783L14.0237 6.52327C14.6486 5.89818 14.9996 5.05049 14.9996 4.16661C14.9996 3.28273 14.6486 2.43503 14.0237 1.80994C13.3986 1.18504 12.5509 0.833984 11.667 0.833984C10.7831 0.833984 9.93543 1.18504 9.31034 1.80994L2.29784 8.9641C1.36008 9.90187 0.833252 11.1737 0.833252 12.4999C0.833252 13.8261 1.36008 15.098 2.29784 16.0358C3.2356 16.9735 4.50748 17.5004 5.83368 17.5004C7.15987 17.5004 8.43175 16.9735 9.36951 16.0358"
          stroke="#666666"
          stroke-width="1.66667"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </>
  );
};

export const AdminAthleteMessage = () => {
  const { staff } = useStaffAuthContext();
  const { athlete_id } = useParams();
  const navigate = useNavigate();

  const [athlete, setAthlete] = useState(null);
  const [messages, setMessages] = useState([]);
  const [replyInputs, setReplyInputs] = useState({});
  const [replyFiles, setReplyFiles] = useState({}); // Track file uploads per message
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [uploading, setUploading] = useState({});

  const fileInputRefs = useRef({});

  const handleFileChange = (messageId, files) => {
    setReplyFiles((prev) => ({
      ...prev,
      [messageId]: Array.from(files),
    }));
  };

  const clearFileInput = (messageId) => {
    if (fileInputRefs.current[messageId]) {
      fileInputRefs.current[messageId].value = "";
    }
    setReplyFiles((prev) => ({ ...prev, [messageId]: [] }));
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const athleteRes = await fetch(
          `${API_URL.GET_ATHLETE_BY_ID}${athlete_id}`,
          {
            headers: { Authorization: `Bearer ${staff.token}` },
          },
        );
        const athleteData = await athleteRes.json();
        if (athleteRes.ok) setAthlete(athleteData);

        const msgRes = await fetch(
          `${API_URL.ADMIN_GET_ATHLETE_ALL_MESSAGES}${athlete_id}`,
          {
            headers: { Authorization: `Bearer ${staff.token}` },
          },
        );
        const msgData = await msgRes.json();
        if (msgRes.ok) {
          // Filter only root messages (no parent_message_id)
          const rootMessages = msgData.messages
            .filter((msg) => !msg.parent_message_id)
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)); // Latest first
          setMessages(rootMessages);
        } else {
          setError(msgData.error || "Failed to load messages");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [athlete_id, staff.token]);

  const handleReplyChange = (messageId, value) => {
    setReplyInputs((prev) => ({ ...prev, [messageId]: value }));
  };

  const sendMessage = async (parentMessageId = null) => {
    const key = parentMessageId || "new";
    try {
      const text = replyInputs[key];
      const files = replyFiles[key] || [];
      if (!text && files.length === 0) return;

      setUploading((prev) => ({ ...prev, [key]: true }));
      setSuccess("");
      setError("");

      const formData = new FormData();
      formData.append("sender_id", staff.id);
      formData.append("sender_type", "amax");
      formData.append("receiver_id", athlete_id);
      formData.append("receiver_type", "athlete");
      formData.append("message", text || "");
      if (parentMessageId) formData.append("message_id", parentMessageId);
      for (let i = 0; i < files.length; i++)
        formData.append("attachments", files[i]);

      const res = await fetch(API_URL.SEND_AMAX_MESSAGE_TO_ATHLETE, {
        method: "POST",
        headers: { Authorization: `Bearer ${staff.token}` },
        body: formData,
      });

      if (!res.ok) throw new Error("Failed to send message");
      const data = await res.json();

      if (parentMessageId) {
        // It's a reply
        setMessages((prev) =>
          prev.map((m) =>
            m._id === parentMessageId
              ? { ...m, replies: [...(m.replies || []), data.data] }
              : m,
          ),
        );
      } else {
        // New root message
        setMessages((prev) => [...prev, data.data]);
      }

      setReplyInputs((prev) => ({ ...prev, [key]: "" }));
      setReplyFiles((prev) => ({ ...prev, [key]: [] }));
      setSuccess(parentMessageId ? "Reply sent!" : "Message sent!");
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleReplyDelete = async (message, replyId) => {
    if (!window.confirm("Are you sure you want to delete this reply?")) return;
    try {
      const res = await fetch(`${API_URL.DELETE_ATHLETE_MESSAGE}/${replyId}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${staff.token}` },
      });
      if (!res.ok) throw new Error("Failed to delete reply");

      setMessages((prev) =>
        prev.map((m) =>
          m._id === message._id
            ? { ...m, replies: m.replies.filter((r) => r._id !== replyId) }
            : m,
        ),
      );

      setSuccess("Reply deleted successfully!");
      setError("");
    } catch (err) {
      setError(err.message);
    }
  };

  const handleAttachmentDelete = async (msgId, filePath) => {
    if (!window.confirm("Delete this attachment?")) return;
    try {
      const res = await fetch(`${API_URL.DELETE_ATTACHMENT}${msgId}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${staff.token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ filePath }),
      });
      if (!res.ok) throw new Error("Failed to delete attachment");

      setMessages((prev) =>
        prev.map((m) => {
          if (m._id === msgId)
            return {
              ...m,
              attachments: m.attachments.filter((a) => a !== filePath),
            };
          return {
            ...m,
            replies: m.replies?.map((r) =>
              r._id === msgId
                ? {
                    ...r,
                    attachments: r.attachments.filter((a) => a !== filePath),
                  }
                : r,
            ),
          };
        }),
      );

      setSuccess("Attachment deleted successfully!");
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="md:flex md:h-screen">
      <StaffSideMenu />
      <div className="md:flex-1 bg-bgBase overflow-y-auto max-md:pt-4 pb-5">
        <div className="shadow md:sticky md:top-0 z-30">
          <div className="-mb-4">
            {athlete && <AthleteProfileCard athlete={athlete} />}
          </div>
          <AthleteTabMenu active="message" />
        </div>

        <div className="pt-10 px-5 md:px-8 xl:px-16">
          <div className="w-full max-w-[1336px]">
            {/* New Message Box */}
            <div className="p-4 md:p-6 bg-white border border-tblBorder rounded-14 mb-6">
              <h3 className="text-dark font-inter font-medium text-xl mb-4">
                Send New Message
              </h3>
              <div className="space-y-2">
                <div>
                  <textarea
                    value={replyInputs["new"] || ""}
                    onChange={(e) =>
                      setReplyInputs((prev) => ({
                        ...prev,
                        ["new"]: e.target.value,
                      }))
                    }
                    className="w-full bg-[#F3F4F6] border border-tblBorder p-3 rounded-lg placeholder:text-slate100 text-sm font-inter font-normal leading-tight outline-none mesageInput"
                    rows="3"
                    placeholder="Write a new message..."
                    disabled={uploading["new"]}
                  />
                  <div className="flex flex-wrap justify-between items-end gap-8">
                    <div>
                      <div className="relative cursor-pointer w-fit block pt-1">
                        <span className="inline-flex items-center gap-2 cursor-pointer bg-bgBase border border-tblBorder py-2.5 px-6 rounded-lg text-dark whitespace-nowrap font-inter text-sm font-medium hover:bg-tblHead">
                          <LinkIcon /> Attach files
                        </span>
                        <input
                          type="file"
                          multiple
                          className="opacity-0 cursor-pointer absolute inset-0"
                          ref={(el) => (fileInputRefs.current["new"] = el)}
                          onChange={(e) =>
                            handleFileChange("new", e.target.files)
                          }
                          disabled={uploading["new"]}
                        />
                      </div>
                      {replyFiles["new"]?.length > 0 && (
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          {replyFiles["new"].map((file, i) => (
                            <div
                              key={i}
                              className="flex items-center justify-between font-inter font-medium leading-3 text-xs bg-gray-100 px-2 py-2 rounded"
                            >
                              <span>{file.name}</span>
                              <button
                                className="text-red-500 text-base ml-2"
                                onClick={() =>
                                  setReplyFiles((prev) => ({
                                    ...prev,
                                    ["new"]: prev["new"].filter(
                                      (_, idx) => idx !== i,
                                    ),
                                  }))
                                }
                              >
                                ✖
                              </button>
                            </div>
                          ))}
                          <button
                            onClick={() => clearFileInput("new")}
                            className="px-2 py-2 bg-dark text-white font-inter font-normal text-sm leading-3 rounded-md flex-none"
                          >
                            Clear all
                          </button>
                        </div>
                      )}
                    </div>
                    <div>
                      <button
                        onClick={() => sendMessage()}
                        className={`px-6 py-2.5 bg-dark text-white font-inter font-normal text-sm leading-tight rounded-lg  ${
                          uploading["new"]
                            ? "cursor-not-allowed"
                            : "hover:bg-black"
                        }`}
                        disabled={uploading["new"]}
                      >
                        {uploading["new"] ? "Uploading..." : "Send Message"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Messages List */}
            {/* <div className="p-4 md:p-6 bg-white border border-tblBorder rounded-14"> */}
            {error && (
              <div className="bg-red-300 p-2 rounded-md my-2">
                <span className="font-inter font-normal text-sm text-red-500">
                  {error}
                </span>
              </div>
            )}
            {success && (
              <div className="bg-green-300 p-2 rounded-md my-2">
                <span className="font-inter font-normal text-sm text-green-500">
                  {success}
                </span>
              </div>
            )}
            {loading ? (
              <p className="my-3 text-dark font-inter font-normal text-sm">
                Loading messages...
              </p>
            ) : messages.length > 0 ? (
              messages.map((msg) => (
                <div
                  key={msg._id}
                  className={`bg-white p-4 md:p-6 rounded-14 border border-tblBorder mb-6 last:mb-0 ${!msg.read ? "bg-yellow-100" : ""}`}
                >
                  <div className="text-end bg-[#2B7FFF] rounded-2xl py-3 px-4">
                    {/* Root message */}
                    <h2 className="text-white font-inter font-semibold text-sm leading-none">
                      {msg.senderName}
                    </h2>
                    <p className="text-white font-inter font-normal text-sm">
                      {msg.message}
                    </p>
                    {msg.attachments?.length > 0 && (
                      <div className="my-2">
                        {msg.attachments.map((file, i) => {
                          const cleanPath = file.replace(/^\/+/, "");
                          return (
                            <div
                              key={i}
                              className="flex flex-wrap justify-end items-center gap-3 font-inter font-medium text-white"
                            >
                              <a
                                href={`${API_URL.SERVER_PATH}${cleanPath}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-light bg-blue-400 rounded-sm text-xs leading-none py-1 px-2"
                              >
                                {cleanPath.split("/").pop()}
                              </a>
                              <button
                                onClick={() =>
                                  handleAttachmentDelete(msg._id, file)
                                }
                                className="bg-white py-1 px-2 rounded-sm hover:bg-gray-200"
                              >
                                <RiDeleteBin4Line color="#666" size={20} />
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <p className="font-inter font-normal text-xs text-white">
                      {new Date(msg.timestamp).toLocaleDateString()}
                    </p>
                  </div>
                  <p className="text-slate100 font-inter font-normal text-xs text-end mt-1">
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </p>

                  {/* Replies */}
                  <div className="mt-4 pl-4 border-l-2 border-gray-300">
                    <h3 className="font-inter font-semibold mb-2">Replies</h3>
                    {msg.replies?.length > 0 ? (
                      msg.replies.map((r) => (
                        <>
                          <div
                            key={r._id}
                            className="mb-1 p-3 border rounded-2xl bg-[#F3F4F6]"
                          >
                            <p className="font-inter font-medium mb-1">
                              {r.senderName}
                            </p>
                            <p className="font-inter font-normal">
                              {r.message}
                            </p>
                            {r.attachments?.length > 0 && (
                              <div className="mt-2">
                                {r.attachments.map((file, i) => {
                                  const cleanPath = file.replace(/^\/+/, "");
                                  return (
                                    <div
                                      key={i}
                                      className="flex items-center gap-2 font-inter font-medium"
                                    >
                                      <a
                                        href={`${API_URL.SERVER_PATH}${cleanPath}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 font-inter underline"
                                      >
                                        {cleanPath.split("/").pop()}
                                      </a>
                                      <button
                                        onClick={() =>
                                          handleAttachmentDelete(r._id, file)
                                        }
                                        className="bg-white block py-1 px-2 rounded-sm hover:bg-gray-200 mt-3"
                                      >
                                        <RiDeleteBin4Line
                                          color="#666"
                                          size={20}
                                        />
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            <p className="font-inter font-normal text-xs text-gray-500">
                              {new Date(r.timestamp).toLocaleDateString()}
                            </p>
                            <button
                              onClick={() => handleReplyDelete(msg, r._id)}
                              className="bg-white block py-1 px-2 rounded-sm hover:bg-gray-200 mt-3"
                            >
                              <RiDeleteBin4Line color="#666" size={20} />
                            </button>
                          </div>
                          <span className="text-slate100 font-inter font-normal text-xs">
                            {new Date(r.timestamp).toLocaleTimeString()}
                          </span>
                        </>
                      ))
                    ) : (
                      <div className="p-3 border rounded-2xl bg-[#F3F4F6]">
                        <p className="text-sm font-inter font-normal text-slate100">
                          No replies yet.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Reply Box */}
                  <div className="mt-4 pt-4 border-t border-[#F3F4F6]">
                    <textarea
                      value={replyInputs[msg._id] || ""}
                      onChange={(e) =>
                        handleReplyChange(msg._id, e.target.value)
                      }
                      className="w-full bg-[#F3F4F6] border border-tblBorder p-3 rounded-full placeholder:text-slate100 text-sm font-inter font-normal leading-tight outline-none mesageInput"
                      rows="1"
                      placeholder="Write a reply..."
                      disabled={uploading[msg._id]}
                    />
                    <div className="flex flex-wrap justify-between items-end gap-8">
                      <div className="mt-2">
                        <div className="relative cursor-pointer inline-block">
                          <span className="inline-flex items-center gap-2 bg-bgBase cursor-pointer border border-tblBorder py-2.5 px-6 rounded-lg text-dark whitespace-nowrap font-inter text-sm font-medium hover:bg-tblHead">
                            <LinkIcon /> Attach files
                          </span>
                          <input
                            type="file"
                            multiple
                            className="opacity-0 cursor-pointer absolute inset-0"
                            ref={(el) => (fileInputRefs.current[msg._id] = el)}
                            onChange={(e) =>
                              handleFileChange(msg._id, e.target.files)
                            }
                            disabled={uploading[msg._id]}
                          />
                        </div>
                        {/* file name */}
                        {replyFiles[msg._id]?.length > 0 && (
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            {replyFiles[msg._id].map((file, i) => (
                              <div
                                key={i}
                                className="flex items-center justify-between font-inter font-medium leading-3 text-xs bg-gray-100 px-2 py-2 rounded"
                              >
                                <span>{file.name}</span>
                                <button
                                  className="text-red-500 text-xs ml-2"
                                  onClick={() =>
                                    setReplyFiles((prev) => ({
                                      ...prev,
                                      [msg._id]: prev[msg._id].filter(
                                        (_, idx) => idx !== i,
                                      ),
                                    }))
                                  }
                                >
                                  ✖
                                </button>
                              </div>
                            ))}
                            <button
                              onClick={() => clearFileInput(msg._id)}
                              className="px-2 py-2 bg-dark text-white font-inter font-normal text-sm leading-3 rounded-md flex-none"
                            >
                              Clear all
                            </button>
                          </div>
                        )}
                      </div>
                      {/* upload btn */}
                      <div className="">
                        <button
                          onClick={() => sendMessage(msg._id)}
                          className={`px-6 py-2.5 bg-dark text-white font-inter font-normal text-sm leading-tight rounded-lg  ${
                            uploading[msg._id]
                              ? "cursor-not-allowed"
                              : "hover:bg-black"
                          }`}
                          disabled={uploading[msg._id]}
                        >
                          {uploading[msg._id] ? "Uploading..." : "Send Reply"}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm font-inter font-normal text-slate100">
                No messages found for this athlete.
              </p>
            )}
            {/* </div> */}
          </div>
        </div>
      </div>
    </div>
  );
};
